import React, { useState } from 'react';
import { WithRestrictionsRender } from '#packages/editorRestrictions';
import {
  ContextMenu,
  ContextMenuAction,
  ContextMenuContent,
  SectionDivider,
  Divider,
  Tooltip,
} from '@wix/wix-base-ui';
import { SymbolWrapper } from '#packages/baseUI';
import { translate } from '#packages/i18n';
import styles from './footerContextMenu.scss';
import type { MenuAddItemAction } from '#packages/menu';
import { MenuDeprecationNotificationBanner, constants } from '#packages/menu';
import experiments from 'experiment';
import type { MenuItem } from '@wix/document-services-types';
import { biLogger } from '#packages/util';
import { pagesPanelUpdateMenuMessageDisplaySrc38Evid4 } from '@wix/bi-logger-components-data/v2';

interface FooterContextMenuProps {
  menuItems: MenuItem[];
  addActions: MenuAddItemAction[];
  customButton: React.ReactNode;
  onContextMenuItemsHover: (category: string, target: string) => void;
  category: string;
  isOldMenu: boolean;

  openHelpCenter: (article: string) => void;
}

const FooterContextMenu: React.FC<FooterContextMenuProps> = ({
  menuItems,
  addActions,
  customButton,
  onContextMenuItemsHover,
  category,
  isOldMenu,
  openHelpCenter,
}) => {
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const hasSubMenuItems = !!menuItems?.some(
    (item) => item.items && item.isVisible && item.items.length > 0,
  );
  const isOldMenuWithSubItems = hasSubMenuItems && isOldMenu;

  const { PANEL_TYPES } = constants;

  // any positive numeric value will fill the full width
  const maxWidth = 1;

  return (
    <div>
      <WithRestrictionsRender name={'menu-panel_add-item'}>
        {({ disabled }) => (
          <ContextMenu
            onToggle={(isOpen: boolean) => {
              if (isOpen && isOldMenuWithSubItems) {
                biLogger.report(
                  pagesPanelUpdateMenuMessageDisplaySrc38Evid4({
                    panelType: PANEL_TYPES.SITE_PAGES_PANEL,
                  }),
                );
              }
              setIsContextMenuOpen(isOpen);
            }}
            isOpen={isContextMenuOpen}
            disabled={disabled}
            alignment="RIGHT"
            dataHook={'footer-context-menu'}
            maxWidth={maxWidth}
            customButton={customButton}
          >
            <ContextMenuContent>
              {addActions.map((action) => {
                if (action.type === 'itemsTitle') {
                  return (
                    <div className={styles.dividerWrapper}>
                      <SectionDivider>
                        <div className={styles.dividerContent}>
                          <span>
                            {translate(
                              'custom_menu_manage_menu_items_section_title',
                            )}
                          </span>
                        </div>
                      </SectionDivider>
                    </div>
                  );
                }
                if (action.type === 'divider') {
                  return <Divider direction={'horizontal'} />;
                }
                return (
                  <ContextMenuAction
                    key={action.symbolName}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    dataHook={action.automationId}
                    automationId={action.automationId}
                    className={`sortable-list-item__context-menu-action ${action.automationId}`}
                  >
                    <Tooltip
                      interactive
                      alignment="TOP"
                      disabled={!action.isShowTooltip}
                      content={action.tooltipContent}
                      automationId={`${action.automationId}-tooltip`}
                    >
                      <div
                        className={styles.contextMenuActionWrapper}
                        onMouseEnter={() =>
                          onContextMenuItemsHover(category, action.automationId)
                        }
                      >
                        <SymbolWrapper
                          className="symbol sortable-list-item__context-menu-action-icon"
                          name={action.symbolName}
                        />
                        <span className="sortable-list-item__context-menu-action-text">
                          {action.title}
                        </span>
                      </div>
                    </Tooltip>
                  </ContextMenuAction>
                );
              })}
            </ContextMenuContent>
            {experiments.isOpen('se_openMenuDeprecationNotificationBanner') &&
              isOldMenuWithSubItems && (
                <div className={styles.MenuDeprecationNotificationBanner}>
                  <MenuDeprecationNotificationBanner
                    setIsContextMenuOpen={setIsContextMenuOpen}
                    openHelpCenter={openHelpCenter}
                    menuItems={menuItems}
                    panelType={PANEL_TYPES.SITE_PAGES_PANEL}
                  />
                </div>
              )}
          </ContextMenu>
        )}
      </WithRestrictionsRender>
    </div>
  );
};

export default FooterContextMenu;
